import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Firestore } from 'firebase/firestore'
import {
  listenToRemoteHarnessState,
  listenToRemoteHoleCards,
  readRemoteHarnessStateFromServer,
  readRemoteHoleCardsFromServer,
  recordHarnessCheck,
} from '../harness/remoteGameService'
import type { RemoteHarnessState, RemoteHoleCards } from '../harness/remoteTypes'

const SERVER_RECONCILE_INTERVAL_MS = 4_000

export interface RemoteHarnessPhoneState {
  /** Null for every ordinary Poker Pot room and before a remote dealer starts. */
  state: RemoteHarnessState | null
  /** Null until this exact player's current private hand is available. */
  holeCards: RemoteHoleCards | null
  loading: boolean
  fromCache: boolean
  error: Error | null
  submitCheck: () => Promise<void>
}

function asError(reason: unknown): Error {
  return reason instanceof Error ? reason : new Error(String(reason))
}

/**
 * Subscribes only to the member-readable public dealer projection and this
 * authenticated player's exact private-hand document. It never lists hands,
 * so another player's cards cannot enter the phone's state by accident.
 */
export function useRemoteHarness(
  db: Firestore,
  code: string,
  playerUid: string,
): RemoteHarnessPhoneState {
  const [state, setState] = useState<RemoteHarnessState | null>(null)
  const [rawHoleCards, setRawHoleCards] = useState<RemoteHoleCards | null>(null)
  const [stateLoaded, setStateLoaded] = useState(false)
  const [handLoaded, setHandLoaded] = useState(false)
  const [stateFromCache, setStateFromCache] = useState(true)
  const [handFromCache, setHandFromCache] = useState(true)
  const [stateError, setStateError] = useState<Error | null>(null)
  const [handError, setHandError] = useState<Error | null>(null)

  useEffect(() => {
    setState(null)
    setRawHoleCards(null)
    setStateLoaded(false)
    setHandLoaded(false)
    setStateFromCache(true)
    setHandFromCache(true)
    setStateError(null)
    setHandError(null)

    return listenToRemoteHarnessState(
      db,
      code,
      (value, fromCache) => {
        setState(value)
        setStateFromCache(fromCache)
        setStateLoaded(true)
        setStateError(null)
        if (!value) {
          // Missing is the normal, successful result for a non-harness room.
          setRawHoleCards(null)
          setHandLoaded(true)
          setHandFromCache(fromCache)
        }
      },
      (reason) => {
        setStateLoaded(true)
        setStateError(asError(reason))
      },
    )
  }, [code, db, playerUid])

  useEffect(() => {
    if (!state) return
    setHandLoaded(false)
    return listenToRemoteHoleCards(
      db,
      code,
      playerUid,
      (value, fromCache) => {
        setRawHoleCards(value)
        setHandFromCache(fromCache)
        setHandLoaded(true)
        setHandError(null)
      },
      (reason) => {
        setHandLoaded(true)
        setHandError(asError(reason))
      },
    )
  }, [code, db, playerUid, Boolean(state)])

  useEffect(() => {
    if (!state) return
    let active = true
    let inFlight = false

    const reconcile = async () => {
      if (!active || inFlight || document.hidden || !navigator.onLine) return
      inFlight = true
      try {
        const [serverState, serverHand] = await Promise.all([
          readRemoteHarnessStateFromServer(db, code),
          readRemoteHoleCardsFromServer(db, code, playerUid),
        ])
        if (!active) return
        setState(serverState)
        setRawHoleCards(serverHand)
        setStateFromCache(false)
        setHandFromCache(false)
        setStateLoaded(true)
        setHandLoaded(true)
        setStateError(null)
        setHandError(null)
      } catch {
        // Realtime listeners remain authoritative. A failed direct read usually
        // means a temporarily offline phone, so retry after connectivity returns.
      } finally {
        inFlight = false
      }
    }

    const reconcileWhenAvailable = () => { if (!document.hidden) void reconcile() }
    const interval = window.setInterval(() => void reconcile(), SERVER_RECONCILE_INTERVAL_MS)
    document.addEventListener('visibilitychange', reconcileWhenAvailable)
    window.addEventListener('focus', reconcileWhenAvailable)
    window.addEventListener('online', reconcileWhenAvailable)
    window.addEventListener('pageshow', reconcileWhenAvailable)
    void reconcile()

    return () => {
      active = false
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', reconcileWhenAvailable)
      window.removeEventListener('focus', reconcileWhenAvailable)
      window.removeEventListener('online', reconcileWhenAvailable)
      window.removeEventListener('pageshow', reconcileWhenAvailable)
    }
  }, [code, db, playerUid, Boolean(state)])

  const holeCards = useMemo(() => {
    if (!state || !rawHoleCards) return null
    return rawHoleCards.uid === playerUid
      && rawHoleCards.runId === state.runId
      && rawHoleCards.handNumber === state.handNumber
      ? rawHoleCards
      : null
  }, [playerUid, rawHoleCards, state])

  const submitCheck = useCallback(async () => {
    if (!state) throw new Error('The remote test is not ready for Check.')
    if (state.phase !== 'betting'
      || state.actorUid !== playerUid
      || !state.canCheck
      || !state.prompt.legalActions.includes('check')) {
      throw new Error('Check is not available to you now.')
    }
    await recordHarnessCheck(db, code, playerUid, state.turnToken)
  }, [code, db, playerUid, state])

  return {
    state,
    holeCards,
    loading: !stateLoaded || Boolean(state && !handLoaded),
    fromCache: stateFromCache || Boolean(state && handFromCache),
    error: stateError ?? handError,
    submitCheck,
  }
}
